import { FC } from 'react';
import { Button } from '@cfa/react-core';
import { IconWithShadow } from '../Styled/IconWithShadow';
import styled from 'styled-components';
import { themeSizes, themeColors } from '../../styles/globalStyleVars';
import {
  IconAlertOctagon,
  IconCalendarEvent,
  IconLockAccess,
  IconTicket,
} from '@tabler/icons-react';
import { generateErrorOptions } from '../../utils/config/errorConfig';
import { TypographyStyled } from '../Styled/Typography';

const ErrorRow = styled.div<{ $page: string }>`
  margin-bottom: ${themeSizes.sm};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  &:first-of-type {
    margin-top: ${({ $page }) =>
      ['home', 'modal'].includes($page)
        ? themeSizes.md
        : $page === 'search'
          ? '80px'
          : $page === 'profile'
            ? themeSizes.xxl
            : '88px'};
    margin-bottom: ${themeSizes.sm};
  }
  &:last-of-type {
    ${({ $page }) => $page !== 'modal' && 'margin-bottom: 328px'};
  }
`;

interface Props {
  btnClick: () => void;
  errorType: string;
  page: string;
  isConfidential?: boolean | undefined;
  btnPrimary?: boolean;
}

const FetchError: FC<Props> = ({
  btnClick,
  errorType,
  page,
  isConfidential,
  btnPrimary,
}) => {
  const errorOptions = generateErrorOptions(page, errorType, isConfidential);

  if (!errorOptions) return <></>;

  const generateIconComponent = () => {
    const tablerIconProps = {
      size: 38,
      color: themeColors.colorGray_6,
    };
    switch (errorType) {
      case 'insufficient':
        return {
          tablerIcon: <IconLockAccess {...tablerIconProps} />,
          iconName: 'Lock Access',
        };

      case 'ticketNotFound':
        return {
          tablerIcon: <IconTicket {...tablerIconProps} />,
          iconName: 'Ticket',
        };
      case 'appointmentNotFound':
        return {
          tablerIcon: <IconCalendarEvent {...tablerIconProps} />,
          iconName: 'Calendar Event',
        };
      default:
        return {
          tablerIcon: <IconAlertOctagon {...tablerIconProps} />,
          iconName: 'Alert Octagon',
        };
    }
  };

  return (
    <>
      <ErrorRow className='row' $page={page} id='fetch-error'>
        <IconWithShadow {...generateIconComponent()} />
        <TypographyStyled
          text={errorOptions.errorTitle}
          variant='h3'
          style={{ marginTop: themeSizes.md }}
        />
      </ErrorRow>
      <ErrorRow className='row' $page={page}>
        <TypographyStyled
          text={errorOptions.errorMessage}
          style={{ whiteSpace: 'pre-line', marginBottom: themeSizes.lg }}
        />
        {errorOptions.errorSecondaryMessage && (
          <TypographyStyled text={errorOptions.errorSecondaryMessage} />
        )}
      </ErrorRow>
      {errorOptions.showButton && (
        <ErrorRow className='row' $page={page}>
          <Button
            color={btnPrimary ? 'primary' : 'secondary'}
            onPress={btnClick}
            size='sm'
            variant='outlined'
            style={{ maxWidth: 'fit-content' }}
            data-testid='fetch-error-button'
          >
            {errorOptions.buttonText}
          </Button>
        </ErrorRow>
      )}
    </>
  );
};
export default FetchError;
